import React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import "../../../customStyles/bootstrap_table.css";
import CheckBox from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlank from "@material-ui/icons/CheckBoxOutlineBlank";
import { SERVER_URL } from "../../../api";
import { Button } from "@material-ui/core";

const { SearchBar, ClearSearchButton } = Search;

export const buttonsStyle = {
  maxWidth: "30px",
  maxHeight: "30px",
  minWidth: "30px",
  minHeight: "30px",
  marginLeft: "1em",
};

export function booleanFormatter(cell) {
  return cell ? (
    <CheckBox style={{ color: "#D91E48" }} />
  ) : (
    <CheckBoxOutlineBlank style={{ color: "#D91E48" }} />
  );
}

export function longTextFormatter(cell) {
  return (
    <td className="truncate">
      <div dangerouslySetInnerHTML={{ __html: cell }}></div>
    </td>
  );
}

function extractContent(s) {
  if (!s) return "----";
  var span = document.createElement("span");
  span.innerHTML = s;
  return span.textContent || span.innerText;
}

export function substringFormatter(cell) {
  const text = extractContent(cell);
  return text.length > 50 ? text.substring(0, 50) + "..." : text;
}

const buildDate = (date) => {
  if (Object.prototype.toString.call(date) !== "[object Date]") return;
  const displayDate = `${date.getDate()}/${date.getMonth() +
    1}/${date.getFullYear()}  ${date.getHours()}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
  return displayDate;
};

export function dateFormatter(cell) {
  if (!cell) return "---";

  let date = new Date(cell);
  if (isNaN(date.getTime())) return "---";

  date = buildDate(date);

  return date;
}

export function imageFormatter(imageURL) {
  return imageURL ? (
    <img src={`${SERVER_URL}/${imageURL}`} width={50}></img>
  ) : (
    <div></div>
  );
}

const customTotal = (from, to, size) => (
  <span
    className="react-bootstrap-table-pagination-total"
    style={{ paddingLeft: "5px" }}
  >
    Showing {from}-{to} of {size} results
  </span>
);

// Opciones de tamaño de página por defecto
const DEFAULT_SIZE_PER_PAGE_LIST = [10, 25, 50, 100];

// Función para crear paginación con persistencia
const createPagination = (
  pageKey,
  onPageChange,
  onSizePerPageChange,
  disablePersistence = false,
  sizePerPage = 100,
  sizePerPageList = DEFAULT_SIZE_PER_PAGE_LIST
) => {
  // Cargar página guardada desde localStorage solo si no está deshabilitada la persistencia
  const savedPage = disablePersistence
    ? null
    : localStorage.getItem(`${pageKey}_currentPage`);
  const initialPage = savedPage ? parseInt(savedPage, 10) : 1;

  // Convertir la lista de números a formato requerido por react-bootstrap-table2-paginator
  const sizePerPageOptions = sizePerPageList.map((size) => ({
    text: size.toString(),
    value: size,
  }));

  return paginationFactory({
    page: initialPage,
    sizePerPage: sizePerPage,
    sizePerPageList: sizePerPageOptions,
    lastPageText: ">>",
    firstPageText: "<<",
    nextPageText: ">",
    prePageText: "<",
    showTotal: true,
    paginationTotalRenderer: customTotal,
    alwaysShowAllBtns: false,
    onPageChange: function(page, sizePerPage) {
      // Guardar página actual en localStorage solo si no está deshabilitada la persistencia
      if (!disablePersistence) {
        localStorage.setItem(`${pageKey}_currentPage`, page.toString());
      }
      if (onPageChange) onPageChange(page, sizePerPage);
    },
    onSizePerPageChange: function(page, sizePerPage) {
      if (!disablePersistence) {
        localStorage.setItem(`${pageKey}_currentPage`, page.toString());
      }
      if (onSizePerPageChange) onSizePerPageChange(page, sizePerPage);
    },
  });
};

const rowClasses = (row) => (row.active === false ? "inactive-row" : "");

// Componente interno para el contenido de la tabla
function TableContent({
  props,
  searchText,
  setSearchText,
  pageKey,
  pagination,
  setOpenModalNoticias,
  tableProps,
  externalSearch, // Si es true, no renderizar SearchBar interno
  defaultSorted, // Ordenamiento por defecto (cuando no hay remoteSort)
  noPagination,
  remoteSort, // Si es true, la ordenación se hace en el backend
  sort, // { dataField, order } para ordenación remota controlada
  onSortChange, // (sortField, sortOrder) cuando el usuario cambia la columna ordenada
}) {
  // Ref para el contenedor del SearchBar (solo si no es búsqueda externa)
  const searchContainerRef = React.useRef(null);
  // Ref para tener siempre el onSearch actual y no depender de closure en el listener
  const searchPropsRef = React.useRef(props.searchProps);
  searchPropsRef.current = props.searchProps;

  // Listener al input: actualizar estado, localStorage Y notificar al ToolkitProvider al instante.
  // Así no hace falta un useEffect que sincronice después (que causaba sobrescritura al escribir rápido).
  React.useEffect(() => {
    if (!externalSearch && searchContainerRef.current) {
      const input = searchContainerRef.current.querySelector(
        'input[type="text"]'
      );
      if (input) {
        const handleInputChange = (e) => {
          const value = e.target.value || "";
          // Un solo camino: onSearch actualiza estado y localStorage en el padre (handleSearchChange)
          if (searchPropsRef.current && searchPropsRef.current.onSearch) {
            searchPropsRef.current.onSearch(value);
          }
        };

        input.addEventListener("input", handleInputChange);

        return () => {
          input.removeEventListener("input", handleInputChange);
        };
      }
    }
  }, [pageKey, setSearchText, externalSearch]);

  // Restaurar texto desde localStorage solo al montar (o al cambiar pageKey), no en cada keystroke.
  // Así evitamos que un timeout sobrescriba el input cuando el usuario escribe rápido.
  const hasRestoredRef = React.useRef(false);
  React.useEffect(() => {
    if (!externalSearch && !hasRestoredRef.current) {
      hasRestoredRef.current = true;
      const saved = localStorage.getItem(`${pageKey}_searchText`);
      if (saved && props.searchProps && props.searchProps.onSearch) {
        const timer = setTimeout(() => {
          if (searchContainerRef.current) {
            const input = searchContainerRef.current.querySelector(
              'input[type="text"]'
            );
            if (input) {
              input.value = saved;
              props.searchProps.onSearch(saved);
            }
          }
        }, 0);
        return () => clearTimeout(timer);
      }
    }
    return undefined;
  }, [pageKey, externalSearch]); // Solo al montar o cambiar pageKey; no incluimos searchText ni props.searchProps

  return (
    <div ref={!externalSearch ? searchContainerRef : null}>
      {!externalSearch && (
        <>
          <SearchBar placeholder="Search" {...props.searchProps} />
          <ClearSearchButton
            className="ml-3"
            {...props.searchProps}
            onClick={() => {
              setSearchText("");
              localStorage.removeItem(`${pageKey}_searchText`);
              localStorage.removeItem(`${pageKey}_currentPage`);
              props.searchProps.onSearch("");
              // Limpiar el input en el DOM por si la librería no actualiza el valor controlado
              if (searchContainerRef.current) {
                const input = searchContainerRef.current.querySelector(
                  'input[type="text"]'
                );
                if (input) input.value = "";
              }
            }}
          />
        </>
      )}
      {setOpenModalNoticias && (
        <div className="d-flex w-100 justify-content-end">
          <Button
            onClick={() => setOpenModalNoticias(true)}
            variant="outlined"
            color="primary"
            style={{ marginRight: "20px" }}
          >
            Add
          </Button>
        </div>
      )}
      {!externalSearch && (
        <>
          <br />
          <br />
        </>
      )}
      <BootstrapTable
        wrapperClasses="table-responsive"
        classes="table table-head-custom table-vertical-center overflow-hidden"
        bordered={false}
        pagination={noPagination ? false : pagination}
        striped
        rowClasses={rowClasses}
        defaultSorted={remoteSort ? undefined : defaultSorted}
        sort={remoteSort && sort ? sort : undefined}
        remote={remoteSort ? { sort: true } : undefined}
        onTableChange={
          remoteSort && onSortChange
            ? (type, { sortField, sortOrder }) => {
                if (type === "sort") onSortChange(sortField, sortOrder);
              }
            : undefined
        }
        {...tableProps}
        {...props.baseProps}
      />
    </div>
  );
}

export default function Table({
  data,
  columns,
  setOpenModalNoticias,
  pageKey = "default", // Clave única para identificar la página y persistir búsqueda/paginación
  defaultSorted = null, // Ordenamiento por defecto: [{ dataField: 'field', order: 'desc' }]
  searchText: externalSearchText, // Texto de búsqueda externo (opcional)
  onSearchChange: externalOnSearchChange, // Handler de búsqueda externo (opcional)
  disablePaginationPersistence = false, // Si es true, no guarda ni carga la página desde localStorage
  sizePerPage, // Número de filas por página (opcional, si no se especifica usa el máximo de sizePerPageList)
  sizePerPageList = DEFAULT_SIZE_PER_PAGE_LIST, // Lista de opciones de tamaño de página
  noPagination = false, // Si es true, oculta la paginación (para paginación por backend)
  remoteSort = false, // Si es true, ordenación por backend (sort + onSortChange obligatorios)
  sort = null, // { dataField, order } para ordenación remota
  onSortChange = null, // (sortField, sortOrder) => void
  ...tableProps
}) {
  // Calcular el tamaño de página por defecto: usar el máximo de la lista si no se especifica
  const defaultSizePerPage =
    sizePerPage !== undefined ? sizePerPage : Math.max(...sizePerPageList);

  // Si se proporcionan searchText y onSearchChange externos, usarlos; si no, usar estado interno
  const isExternalSearch =
    externalSearchText !== undefined && externalOnSearchChange !== undefined;

  // Cargar texto de búsqueda guardado al inicializar (solo si es búsqueda interna)
  const [internalSearchText, setInternalSearchText] = React.useState(() => {
    if (isExternalSearch) return "";
    const saved = localStorage.getItem(`${pageKey}_searchText`);
    return saved || "";
  });

  const searchText = isExternalSearch ? externalSearchText : internalSearchText;
  const setSearchText = isExternalSearch
    ? externalOnSearchChange
    : setInternalSearchText;

  // Crear paginación con persistencia - se recrea cuando cambia pageKey, disablePaginationPersistence, sizePerPage o sizePerPageList
  const pagination = React.useMemo(
    () =>
      createPagination(
        pageKey,
        null,
        null,
        disablePaginationPersistence,
        defaultSizePerPage,
        sizePerPageList
      ),
    [pageKey, disablePaginationPersistence, defaultSizePerPage, sizePerPageList]
  );

  // Ref para el handler de búsqueda del ToolkitProvider
  const searchHandlerRef = React.useRef(null);

  // Sincronizar búsqueda guardada cuando cambia pageKey (solo si es búsqueda interna)
  React.useEffect(() => {
    if (!isExternalSearch) {
      const saved = localStorage.getItem(`${pageKey}_searchText`) || "";
      // Solo actualizar si el valor guardado es diferente al actual
      // Esto evita loops infinitos pero asegura que se restaure al volver
      if (saved !== internalSearchText) {
        setInternalSearchText(saved);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, isExternalSearch]); // Solo cuando cambia pageKey, no cuando cambia searchText

  // Guardar texto de búsqueda cuando cambia (solo si es búsqueda interna)
  const handleSearchChange = React.useCallback(
    (newSearchText) => {
      const trimmedText = newSearchText ? newSearchText.trim() : "";
      if (isExternalSearch) {
        // Si es búsqueda externa, solo llamar al handler externo
        externalOnSearchChange(trimmedText);
      } else {
        // Si es búsqueda interna, manejar estado y localStorage
        setInternalSearchText(trimmedText);
        // Guardar en localStorage (como variable de sesión)
        if (trimmedText) {
          localStorage.setItem(`${pageKey}_searchText`, trimmedText);
        } else {
          localStorage.removeItem(`${pageKey}_searchText`);
          localStorage.removeItem(`${pageKey}_currentPage`);
        }
      }
      // Guardar referencia al handler para poder llamarlo desde fuera
      searchHandlerRef.current = (text) => {
        const trimmed = text ? text.trim() : "";
        if (isExternalSearch) {
          externalOnSearchChange(trimmed);
        } else {
          setInternalSearchText(trimmed);
          if (trimmed) {
            localStorage.setItem(`${pageKey}_searchText`, trimmed);
          } else {
            localStorage.removeItem(`${pageKey}_searchText`);
            localStorage.removeItem(`${pageKey}_currentPage`);
          }
        }
      };
    },
    [pageKey, isExternalSearch, externalOnSearchChange]
  );

  // Sincronizar searchText externo con ToolkitProvider cuando cambia
  // Esto asegura que cuando el usuario escribe en el TextField externo, el ToolkitProvider filtre
  React.useEffect(() => {
    if (isExternalSearch && externalSearchText !== undefined) {
      // Cuando el searchText externo cambia, llamar directamente al onSearch del ToolkitProvider
      // Esto fuerza la actualización del filtro
      if (searchHandlerRef.current) {
        searchHandlerRef.current(externalSearchText);
      } else {
        // Si aún no tenemos la referencia, usar handleSearchChange como fallback
        handleSearchChange(externalSearchText);
      }
    }
  }, [isExternalSearch, externalSearchText, handleSearchChange]);

  // Usar key para forzar re-montaje cuando cambia pageKey y asegurar que se restaure el estado
  return (
    <ToolkitProvider
      key={`table-${pageKey}`}
      bootstrap4
      keyField="id"
      data={data}
      columns={columns}
      search={{
        searchText: searchText || "",
        onSearch: handleSearchChange,
      }}
    >
      {(props) => {
        // Guardar referencia al onSearch del ToolkitProvider para poder llamarlo desde fuera
        if (
          isExternalSearch &&
          props.searchProps &&
          props.searchProps.onSearch
        ) {
          searchHandlerRef.current = props.searchProps.onSearch;
        }
        return (
          <TableContent
            props={props}
            searchText={searchText}
            setSearchText={setSearchText}
            pageKey={pageKey}
            pagination={pagination}
            setOpenModalNoticias={setOpenModalNoticias}
            tableProps={tableProps}
            externalSearch={isExternalSearch}
            defaultSorted={defaultSorted}
            noPagination={noPagination}
            remoteSort={remoteSort}
            sort={sort}
            onSortChange={onSortChange}
          />
        );
      }}
    </ToolkitProvider>
  );
}
